import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Moon, Sun } from 'lucide-react';
import { useTheme } from 'next-themes';
import { AuthForm } from '@/components/auth/AuthForm';
import logoFull from '@/assets/odyvoy-logo-full.png';

export default function Auth() {
  const { user, loading } = useAuth();
  const { resolvedTheme, setTheme } = useTheme();
  const [searchParams] = useSearchParams();
  const [hasReferralDiscount, setHasReferralDiscount] = useState(false);
  const [hasTrialOffer, setHasTrialOffer] = useState(false);
  const [redirectToLanding, setRedirectToLanding] = useState(false);

  useEffect(() => {
    const referralCode = searchParams.get('ref');
    const trialOffer = searchParams.get('trial');

    if (referralCode) {
      localStorage.setItem('referral_code', referralCode);
      setHasReferralDiscount(true);
    }

    if (trialOffer === 'true') {
      localStorage.setItem('start_trial', 'true');
      setHasTrialOffer(true);
    }

    // Cold traffic from search engines should see the landing page first,
    // not a bare signup wall. Only intercept first-time arrivals with no
    // explicit intent (mode/ref/trial) and an external referrer.
    const hasIntent =
      searchParams.get('mode') ||
      searchParams.get('ref') ||
      searchParams.get('trial') ||
      searchParams.get('from');
    if (!hasIntent && typeof document !== 'undefined') {
      const ref = document.referrer || '';
      const isSearchEngine = /(?:google|bing|duckduckgo|yahoo|ecosia|brave)\./i.test(ref);
      if (isSearchEngine) {
        setRedirectToLanding(true);
      }
    }
  }, [searchParams]);

  const defaultTab: 'signup' | 'signin' = searchParams.get('mode') === 'login' ? 'signin' : 'signup';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (user) {
    // Preserve OAuth-consent (or other) return targets. Only allow same-origin
    // relative paths to prevent open-redirect abuse.
    const next = searchParams.get('next');
    if (next && next.startsWith('/') && !next.startsWith('//')) {
      return <Navigate to={next} replace />;
    }
    const startTrial = localStorage.getItem('start_trial');
    if (startTrial === 'true') {
      localStorage.removeItem('start_trial');
    }
    return <Navigate to="/dashboard" replace />;
  }

  if (redirectToLanding) {
    return <Navigate to="/?from=search" replace />;
  }

  return (
    <div className="auth-split min-h-screen bg-background text-foreground">
      <Helmet>
        <title>Sign In or Create Account | OdyVoy - AI Travel Planner</title>
        <meta name="description" content="Sign in to your OdyVoy account or create a new one. Access AI-powered packing lists, travel budgets, and trip planning tools." />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://odyvoy.com/auth" />
      </Helmet>

      <div className="absolute top-4 right-4 z-50">
        <Button
          size="icon"
          variant="ghost"
          className="h-12 w-12 text-primary"
          aria-label={resolvedTheme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
        >
          {resolvedTheme === 'dark' ? <Sun /> : <Moon />}
        </Button>
      </div>

      <h1 className="sr-only">Sign In or Create Your OdyVoy Account</h1>

      <div className="auth-split-grid">
        {/* Left: brand panel */}
        <aside className="auth-brand">
          <Link to="/" className="auth-brand-logo" aria-label="OdyVoy home">
            <img
              src={logoFull}
              alt="OdyVoy - Your Odyssey. Our Voyage!"
              width={1259}
              height={292}
              fetchPriority="high"
              decoding="async"
            />
          </Link>
          <p className="auth-brand-headline">
            Ready for your
            <br />
            <span>next trip?</span>
          </p>
          <p className="auth-brand-tagline">
            Packing, visas, weather, money and local tips — checked before you leave, not at the gate.
          </p>
        </aside>

        {/* Right: form panel */}
        <main className="auth-panel">
          <AuthForm
            defaultTab={defaultTab}
            hasReferralDiscount={hasReferralDiscount}
            hasTrialOffer={hasTrialOffer}
          />
        </main>
      </div>
    </div>
  );
}
